import type {
  ModelProvider,
  ModelProviderSecretType,
  PersistableModelProvider,
} from './model-provider-secrets.types.js';

/**
 * Display labels for the providers in
 * {@link ModelProviderSchema}. Renders inside the provider select and
 * the per-row status cards so the operator sees "Anthropic" rather
 * than the lowercase schema literal.
 */
export const MODEL_PROVIDER_LABELS: Record<ModelProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  google: 'Google',
  xai: 'xAI',
  openrouter: 'OpenRouter',
  cursor: 'Cursor',
  'fireworks-ai': 'Fireworks AI',
  baseten: 'Baseten',
  vercel: 'Vercel AI Gateway',
  nebius: 'Nebius Token Factory',
  runloop: 'Runloop',
};

/**
 * Default upstream base URL for the Nebius Token Factory (an OpenAI-compatible
 * inference API). Applied when a Nebius key carries no custom `baseUrl`: it is
 * the SDK `baseURL` for the platform chat/classifier paths and the gateway
 * upstream at agent launch. Orgs on a different Nebius region or dedicated
 * endpoint override it via the per-key "Base URL" field. Kept in sync with the
 * `nebius` entry in `GATEWAY_PROVIDERS`.
 */
export const NEBIUS_DEFAULT_BASE_URL = 'https://api.tokenfactory.us-central1.nebius.com/v1';

/** Default OpenAI-compatible upstream for the service-backed Runloop provider. */
export const RUNLOOP_MODELS_DEFAULT_BASE_URL = 'https://api.tokenfactory.us-central1.nebius.com/v1';

/**
 * Provider env var that carries a custom API base URL. Only providers with a
 * well-known base-URL override appear here (Anthropic, OpenAI); these match the
 * `baseUrlEnvVar` entries on `GATEWAY_PROVIDERS`. Single source of truth shared
 * by the web (to gate the optional "Base URL" field) and the server (to inject
 * the env var at launch and override the gateway upstream).
 */
export const MODEL_PROVIDER_BASE_URL_ENV_VARS: Partial<Record<ModelProvider, string>> = {
  anthropic: 'ANTHROPIC_BASE_URL',
  openai: 'OPENAI_BASE_URL',
  // Nebius is OpenAI-compatible but carries its own env-var pair
  // (`NEBIUS_API_KEY`/`NEBIUS_BASE_URL`, matching the models.dev `nebius`
  // provider that OpenCode reads) so a Nebius key never collides with a real
  // OpenAI key when both resolve for the same launch.
  nebius: 'NEBIUS_BASE_URL',
  runloop: 'RUNLOOP_MODELS_BASE_URL',
};

/**
 * Whether a provider supports a custom base URL — i.e. it has an entry in
 * {@link MODEL_PROVIDER_BASE_URL_ENV_VARS}. Used to gate the advanced "Base URL"
 * field in the add/edit dialogs and to decide whether to inject the env var.
 */
export function providerSupportsBaseUrl(provider: ModelProvider): boolean {
  return provider in MODEL_PROVIDER_BASE_URL_ENV_VARS;
}

/**
 * Whether a custom base URL is meaningful for a given secret row. A base URL
 * only takes effect for raw `apiKey` rows whose provider exposes a base-URL env
 * var — subscription credentials (Claude Max / Codex) mount through their own
 * path and providers without a base-URL env var have nowhere to apply it. Single
 * source of truth for gating the field in the UI and rejecting/ignoring stray
 * `baseUrl` values on the server (create, update, and gateway resolution).
 */
export function secretSupportsBaseUrl(
  provider: ModelProvider,
  type: ModelProviderSecretType,
): boolean {
  return type === 'apiKey' && providerSupportsBaseUrl(provider);
}

/**
 * Ordered list of providers the manager renders. Matches the schema
 * but pins a UX-friendly order (Anthropic + OpenAI first). OpenRouter
 * is intentionally omitted while the integration is not yet ready —
 * the schema/DB still accept it, but the UI does not surface it.
 * Persistable-only: `runloop` is a service credential with no stored
 * keys, so key-management surfaces never render it.
 */
export const MODEL_PROVIDER_ORDER: readonly PersistableModelProvider[] = [
  'anthropic',
  'openai',
  'cursor',
  'google',
  'fireworks-ai',
  'baseten',
  'xai',
  'vercel',
  'nebius',
];

/**
 * Model-id prefixes used to attribute a bare model id (no `provider/` prefix
 * and not found in the discovery catalog) to a provider. Single source of
 * truth for every heuristic fallback in the UI — both `ui/`'s brand-icon
 * resolver and `web/`'s `deriveProviderFromModelId` consult this table so
 * adding a new model family is a data change, not an `if`-chain edit.
 *
 * Match is case-insensitive `startsWith`. Order matters only between rows of
 * the same provider — the iteration order of this object decides which
 * provider claims an ambiguous prefix, so list the most specific provider
 * before any that would also match.
 *
 * NOTE: this is purely a *fallback*. The discovery catalog
 * (`AgentModelSupportEntry.discoveredModels[].providerId`) is authoritative
 * whenever it is loaded — callers must consult it first.
 */
export const MODEL_PROVIDER_ID_PREFIXES: Record<ModelProvider, readonly string[]> = {
  anthropic: ['claude', 'sonnet', 'opus', 'haiku'],
  openai: ['gpt', 'o1-', 'o3-', 'o4-', 'chatgpt', 'codex'],
  google: ['gemini', 'palm-', 'bison-'],
  xai: ['grok'],
  openrouter: ['openrouter/'],
  cursor: ['cursor-', 'composer-'],
  'fireworks-ai': ['fireworks-ai/'],
  baseten: ['baseten/'],
  vercel: ['vercel/'],
  nebius: ['nebius/', 'glm', 'dedicated/runloop/'],
  runloop: ['runloop/'],
};

/**
 * Look up the provider that owns a bare model id by prefix. Returns
 * `undefined` when no row in {@link MODEL_PROVIDER_ID_PREFIXES} matches so the
 * caller can decide on a default. Pair with the discovery catalog for the
 * authoritative answer — see the note on `MODEL_PROVIDER_ID_PREFIXES`.
 */
export function matchModelProviderByPrefix(modelId: string): ModelProvider | undefined {
  const lower = modelId.toLowerCase();
  if (lower === 'cursor') return 'cursor';
  if (lower === 'composer') return 'cursor';
  for (const [provider, prefixes] of Object.entries(MODEL_PROVIDER_ID_PREFIXES) as [
    ModelProvider,
    readonly string[],
  ][]) {
    if (prefixes.some((p) => lower.startsWith(p))) return provider;
  }
  return undefined;
}

/** Provider id of the Vercel AI Gateway in an agent type's provider-endpoint mapping. */
export const GATEWAY_MODEL_PROVIDER_ID = 'vercel' satisfies ModelProvider;

/**
 * Launch-id prefix of models routed through the Vercel AI Gateway
 * (`vercel/<vendor>/<model>`). A Reflex convention: agent providers strip it
 * before the id reaches the harness.
 */
export const GATEWAY_MODEL_ID_PREFIX: string =
  MODEL_PROVIDER_ID_PREFIXES[GATEWAY_MODEL_PROVIDER_ID][0]!;

/** Whether a launch id routes through the AI Gateway rather than to the vendor directly. */
export function isGatewayRoutedModelId(modelId: string | null | undefined): boolean {
  return modelId?.startsWith(GATEWAY_MODEL_ID_PREFIX) ?? false;
}

/** `vercel/anthropic/claude-sonnet-5` → `anthropic/claude-sonnet-5`; a direct id passes through. */
export function stripGatewayModelIdPrefix(modelId: string): string {
  return isGatewayRoutedModelId(modelId) ? modelId.slice(GATEWAY_MODEL_ID_PREFIX.length) : modelId;
}

/**
 * Vendor prefixes that are redundant with the model family that follows them:
 * stripping `claude-` from `claude-sonnet-4` still leaves the recognizable
 * `sonnet-4`. Only stripped when a family word (a letter) follows, so a
 * version-led id like `claude-3-5-sonnet` keeps its prefix. `gpt-`, `gemini-`,
 * and `grok-` are deliberately absent — there the vendor token *is* the name.
 */
const REDUNDANT_MODEL_PREFIXES = ['claude-'] as const;

/** Trailing dated build stamp, e.g. the `-20250514` in `claude-sonnet-4-20250514`. */
const MODEL_DATE_SUFFIX = /-\d{8}$/;

/**
 * Strip the trailing dated build stamp from a model id:
 * `claude-haiku-4-5-20251001` → `claude-haiku-4-5`. The one definition of the
 * dated-build convention — display shortening here, the rate-card registry
 * derivation, the drift check's canonical keys, and the costs seed lookup all
 * share it, so a provider changing its stamp format is a one-line fix.
 */
export function stripDatedBuildSuffix(modelId: string): string {
  return modelId.replace(MODEL_DATE_SUFFIX, '');
}

/**
 * Per-id display-name overrides for ids whose raw form is unreadable in any UI.
 * The Nebius dedicated deployment id ends in an opaque per-account routing
 * suffix that no heuristic can safely strip (there is no delimiter between the
 * model family and the deployment tag), so the deployment is pinned here to its
 * model family. Consulted by {@link resolveModelDisplayName}, and through it by
 * {@link formatModelLabel} before its general heuristics.
 */
const MODEL_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
  'gpt-6-astra': 'GPT-6 Astra',
  'openai/gpt-6-astra': 'GPT-6 Astra',
  'vercel/openai/gpt-6-astra': 'GPT-6 Astra (AI Gateway)',
  'gpt-6.1-sol': 'GPT-6.1 Sol',
  'openai/gpt-6.1-sol': 'GPT-6.1 Sol',
  'gpt-6-sol': 'GPT-6 Sol',
  'openai/gpt-6-sol': 'GPT-6 Sol',
  'gpt-6-luna': 'GPT-6 Luna',
  'openai/gpt-6-luna': 'GPT-6 Luna',
  'nebius/dedicated/runloop/GLM-5.2-NVFP4-39V6Isdn6nET': 'GLM-5.2 (Nebius)',
  'runloop/dedicated/runloop/GLM-5.2-NVFP4-39V6Isdn6nET': 'GLM-5.2',
};

/**
 * Resolve a model id to the name it should be shown under, without shortening
 * anything else:
 *
 *   nebius/dedicated/runloop/GLM-5.2-NVFP4-39V6Isdn6nET → GLM-5.2
 *   claude-sonnet-4-20250514                            → claude-sonnet-4-20250514
 *
 * Use this on surfaces that deliberately show the full id — preview cards, chip
 * labels, cost tables — where {@link formatModelLabel}'s compaction would drop
 * meaningful detail (the dated build stamp distinguishes billed rows). Like
 * `formatModelLabel` this is display-only; never compare or persist the result.
 */
export function resolveModelDisplayName(modelId: string): string {
  const trimmed = modelId.trim();
  return MODEL_DISPLAY_NAME_OVERRIDES[trimmed] ?? trimmed;
}

/**
 * Shorten a raw model id into a compact label for dense UI (sidebar rows,
 * chips) where the full provider id would overflow. Drops any `provider/` path
 * prefix, the trailing dated build stamp, and a redundant vendor prefix:
 *
 *   claude-sonnet-4-20250514         → sonnet-4
 *   claude-opus-4-8                  → opus-4-8
 *   openrouter/anthropic/claude-...  → opus-4
 *   gpt-5.5                          → gpt-5.5   (no redundant prefix)
 *   opus                             → opus
 *
 * Returns the trimmed input unchanged when nothing matches, so unknown or
 * already-short ids pass through untouched. This is a display heuristic only —
 * never use it to compare or persist model ids.
 */
export function formatModelLabel(modelId: string): string {
  const trimmed = modelId.trim();
  if (!trimmed) return trimmed;

  const override = resolveModelDisplayName(trimmed);
  if (override !== trimmed) return override;

  // Provider-pathed ids (`openrouter/anthropic/claude-opus-4`) → last segment.
  const lastSegment = trimmed.slice(trimmed.lastIndexOf('/') + 1);

  // Drop the dated build stamp some providers append to an otherwise stable id.
  const withoutDate = stripDatedBuildSuffix(lastSegment);

  // Drop a vendor prefix only when a family name (a letter) follows, so
  // `claude-sonnet-4` → `sonnet-4` but `claude-3-5-sonnet` stays intact.
  const lower = withoutDate.toLowerCase();
  for (const prefix of REDUNDANT_MODEL_PREFIXES) {
    if (lower.startsWith(prefix) && /[a-z]/.test(lower.charAt(prefix.length))) {
      return withoutDate.slice(prefix.length);
    }
  }

  return withoutDate;
}

/**
 * Providers that re-route another vendor's models (AI Gateway, etc.). Used by
 * the model picker to demote gateway duplicates when collapsing direct vs
 * gateway routes of the same logical model.
 */
export const GATEWAY_PROVIDER_IDS = [GATEWAY_MODEL_PROVIDER_ID] as const;

/** Whether `providerId` is a multi-vendor gateway rather than a direct vendor. */
export function isGatewayProvider(providerId: string): boolean {
  return (GATEWAY_PROVIDER_IDS as readonly string[]).includes(providerId);
}

/**
 * Collapse key for comparing transport routes of the same logical model.
 * Strips provider path prefixes, dated build stamps, and the redundant
 * `claude-` vendor token, then lowercases and normalizes `.` → `-` so
 * `anthropic/claude-opus-4-8` and `vercel/anthropic/claude-opus-4.8` share a
 * key. Deliberately bypasses {@link MODEL_DISPLAY_NAME_OVERRIDES} — those
 * labels distinguish routes in the UI (`GPT-6 Astra (AI Gateway)`) and would
 * prevent collapse. Display-only; never persist or compare as a model id.
 *
 *   anthropic/claude-opus-4-8              → opus-4-8
 *   vercel/anthropic/claude-opus-4.8       → opus-4-8
 *   anthropic/claude-opus-4-8-fast         → opus-4-8-fast
 *   openai/gpt-6-astra                     → gpt-6-astra
 *   vercel/openai/gpt-6-astra              → gpt-6-astra
 */
export function modelRouteKey(modelId: string): string {
  const trimmed = modelId.trim();
  if (!trimmed) return trimmed;

  const lastSegment = trimmed.slice(trimmed.lastIndexOf('/') + 1);
  const withoutDate = stripDatedBuildSuffix(lastSegment);
  let key = withoutDate.toLowerCase();
  for (const prefix of REDUNDANT_MODEL_PREFIXES) {
    if (key.startsWith(prefix) && /[a-z]/.test(key.charAt(prefix.length))) {
      key = key.slice(prefix.length);
      break;
    }
  }
  return key.replace(/\./g, '-');
}

/**
 * Canonical URL where an operator can mint an API key for each
 * provider. Surfaced as a "Get key" link beside each provider in the
 * `ModelProviderSecretsManager` so first-time setup doesn't require
 * hunting through provider docs. Kept in sync with the integration
 * registry's `docsUrl` values on the server side.
 */
export const MODEL_PROVIDER_DOCS_URLS: Partial<Record<ModelProvider, string>> = {
  anthropic: 'https://console.anthropic.com/settings/keys',
  openai: 'https://platform.openai.com/api-keys',
  google: 'https://aistudio.google.com/apikey',
  xai: 'https://console.x.ai/',
  openrouter: 'https://openrouter.ai/keys',
  cursor: 'https://cursor.com/dashboard',
  'fireworks-ai': 'https://app.fireworks.ai/settings/users/api-keys',
  baseten: 'https://app.baseten.co/settings/api_keys',
  vercel: 'https://vercel.com/docs/ai-gateway/getting-started',
  nebius: 'https://tokenfactory.nebius.com/settings/api-keys',
};
